import { colourByName, doorRules, type ProductRow } from '@planner/config';
import { sanitizeInput, type OrderItem, type ProductDataFile } from '@planner/shared';
import {
  deriveManufacturedSize,
  doorSnapshotFromProduct,
  drawingApprovalBinding,
  historicalSizeChange,
  resolveDoorProduct,
  validateReviewedDoor,
  type DoorConfig,
  type DoorMeasurements,
} from '@planner/shared/doors';
import { hasAcknowledgement, type DoorDraft } from '../stores/doorStore.js';
import { parseMillimetres } from './measurements.js';

function readMeasurements(draft: DoorDraft): DoorMeasurements | undefined {
  const { mode, trade, diy } = draft.measurements;
  if (mode === 'trade') {
    const width = parseMillimetres(trade.width);
    const height = parseMillimetres(trade.height);
    return width && height ? { mode, width, height } : undefined;
  }
  if (mode === 'diy') {
    const widthTop = parseMillimetres(diy.widthTop);
    const widthMiddle = parseMillimetres(diy.widthMiddle);
    const widthBottom = parseMillimetres(diy.widthBottom);
    const height = parseMillimetres(diy.height);
    const heightRight = parseMillimetres(diy.heightRight);
    return widthTop && widthMiddle && widthBottom && height && heightRight
      ? { mode, widthTop, widthMiddle, widthBottom, height, heightRight }
      : undefined;
  }
  return undefined;
}

export type ReviewPreparation =
  { status: 'ready'; door: DoorConfig } | { status: 'invalid'; message: string };

export function prepareReviewDoor(
  draft: DoorDraft,
  catalogue: ProductDataFile<ProductRow>,
  previous?: { door: DoorConfig; item: OrderItem },
): ReviewPreparation {
  const measurements = readMeasurements(draft);
  const size = measurements && deriveManufacturedSize(measurements);
  const lockHeight = parseMillimetres(draft.lock.height);
  const depth = parseMillimetres(draft.measurements.depth);
  const lineId = draft.inputs.reviewLineId;
  if (
    !draft.doorType ||
    !measurements ||
    !size ||
    !lockHeight ||
    !draft.lock.side ||
    !depth ||
    !lineId
  ) {
    return {
      status: 'invalid',
      message: 'Complete your door details before adding it to the Cart.',
    };
  }
  const name = sanitizeInput(draft.inputs.reviewName ?? '', Number.MAX_SAFE_INTEGER);
  if (!name || name.length > doorRules.doorNameMaxLength) {
    return {
      status: 'invalid',
      message: `Door name must be between 1 and ${doorRules.doorNameMaxLength} characters.`,
    };
  }
  const rawQuantity = draft.inputs.reviewQuantity ?? '';
  const quantity = /^[0-9]+$/.test(rawQuantity) ? Number(rawQuantity) : NaN;
  if (
    !Number.isSafeInteger(quantity) ||
    quantity < doorRules.quantity.min ||
    quantity > doorRules.quantity.max
  ) {
    return {
      status: 'invalid',
      message: `Quantity must be a whole number from ${doorRules.quantity.min} to ${doorRules.quantity.max}.`,
    };
  }
  const product = resolveDoorProduct(catalogue.rows, draft.doorType, draft.frameColour, size);
  if (product.status !== 'available') {
    const message =
      product.status === 'ambiguous'
        ? 'More than one product matches this door. Please contact us.'
        : product.status === 'unpriced'
          ? 'A price is not available for this door. Please try again later.'
          : 'This door is no longer available in the selected colour and size.';
    return { status: 'invalid', message };
  }
  const door: DoorConfig = {
    lineId,
    doorName: name,
    doorType: draft.doorType,
    measurements,
    outOfSquareAcknowledged: hasAcknowledgement(draft.measurements, 'width'),
    heightSpreadAcknowledged: hasAcknowledgement(draft.measurements, 'height'),
    depth,
    depthClearanceAcknowledged: hasAcknowledgement(draft.measurements, 'depth'),
    lockHeight,
    lockSide: draft.lock.side,
    colourCode: colourByName(draft.frameColour).id,
    quantity,
    snapshot: doorSnapshotFromProduct(product.product, catalogue.syncedAt),
  };
  const validation = validateReviewedDoor(door);
  if (!validation.valid) {
    return { status: 'invalid', message: validation.message };
  }
  if (previous && !historicalSizeChange(previous.door, previous.item, door)) {
    const unchanged = {
      ...door,
      snapshot: { ...door.snapshot, catalogueSyncedAt: previous.door.snapshot.catalogueSyncedAt },
    };
    if (drawingApprovalBinding(previous.door) === drawingApprovalBinding(unchanged)) {
      return { status: 'ready', door: unchanged };
    }
  }
  return { status: 'ready', door };
}
